import { useState, useRef, useEffect } from "react";
import { getMediaUrl, getVideoDuration } from "../../utils/helpers";
import {
    GetProfileStories, GetStorySignedUrl, uploadWithProgress, CreateStory, DeleteStory
} from "../../services/api";
import StoryViewer from "../../components/Profile/StoryViewer";
import { MdOutlineVideoLibrary } from "react-icons/md";
import { showError } from "../../utils/toast";

export function MyStory() {
    const fileInputRef = useRef(null);

    const [localClips, setLocalClips] = useState([]);
    const [openStory, setOpenStory] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        fetchStories();
    }, []);

    const fetchStories = () => {
        GetProfileStories()
            .then(res => {
                setLocalClips(Array.isArray(res.data) ? res.data : []);
            })
            .catch(console.error);
    };

    /* ➕ ADD CLICK */
    const handleAddClick = () => {
        fileInputRef.current.click();
    };

    /* 🎥 FILE SELECT */
    const handleVideoSelect = async (e) => {
        const files = Array.from(e.target.files).filter(file =>
            file.type.startsWith("video/")
        );

        for (const file of files) {
            const duration = await getVideoDuration(file);
            if (duration > 15) {
                showError("Video must be 15 seconds or less");
                continue;
            }

            const tempId = crypto.randomUUID();
            const previewUrl = URL.createObjectURL(file);

            // 1️⃣ Add temp clip immediately
            setLocalClips(prev => [
                {
                    id: tempId,
                    url: previewUrl,
                    uploading: true,
                    progress: 0,
                },
                ...prev,
            ]);

            // 2️⃣ Upload
            uploadClip(file, tempId);
        }

        e.target.value = "";
    };

    /* ☁️ UPLOAD */
    const uploadClip = async (file, tempId) => {
        try {
            const res = await GetStorySignedUrl();
            const { signedUrl, publicUrl, key } = res.data;

            await uploadWithProgress(signedUrl, file, (percent) => {
                setLocalClips(prev =>
                    prev.map(clip =>
                        clip.id === tempId
                            ? { ...clip, progress: percent }
                            : clip
                    )
                );
            });

            // 3️⃣ Replace temp clip
            setLocalClips(prev =>
                prev.map(clip =>
                    clip.id === tempId
                        ? {
                            id: tempId,
                            url: publicUrl,
                            uploading: false,
                            progress: 100,
                        }
                        : clip
                )
            );

            await CreateStory(key);

        } catch (err) {
            console.error("Upload failed", err);
            setLocalClips(prev => prev.filter(c => c.id !== tempId));
        }
    };

    /* 🗑️ DELETE STORY (FROM VIEWER) */
    const handleDeleteStory = async (storyId) => {
        try {
            await DeleteStory(storyId);

            setLocalClips(prev => {
                const updated = prev.filter(
                    s => s._id !== storyId && s.id !== storyId
                );

                if (updated.length === 0) {
                    setOpenStory(false);
                    return [];
                }

                if (activeIndex >= updated.length) {
                    setActiveIndex(updated.length - 1);
                }

                return updated;
            });
        } catch (err) {
            console.error(err);
            showError("Failed to delete story");
        }
    };

    return (
        <div className="w-full">

            {/* HEADER */}
            <div className="flex gap-2 items-center text-lg font-bold mt-10">
                <MdOutlineVideoLibrary size={30} />
                Clips {localClips.length}
            </div>

            {/* SCROLL ROW */}
            <div className="flex overflow-x-auto no-scrollbar gap-4 py-10">

                {/* ADD BUTTON */}
                <div
                    onClick={handleAddClick}
                    className="w-[90px] h-[90px] flex items-center justify-center
                    rounded-full bg-gray-700 text-white text-4xl cursor-pointer shrink-0"
                >
                    +
                </div>

                {/* FILE INPUT */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    multiple
                    hidden
                    onChange={handleVideoSelect}
                />

                {/* CLIPS */}
                {localClips.map((clip, index) => (
                    <div
                        key={clip._id || clip.id}
                        onClick={() => {
                            setActiveIndex(index);
                            setOpenStory(true);
                        }}
                        className="relative w-[90px] h-[90px] rounded-full
                        border-4 border-red-500 overflow-hidden shrink-0 cursor-pointer"
                    >
                        <video
                            src={getMediaUrl(clip.url)}
                            className="w-full h-full object-cover"
                            muted
                            preload="metadata"
                        />

                        {clip.uploading && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <ProgressCircle progress={clip.progress} />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* STORY VIEWER */}
            {openStory && (
                <StoryViewer
                    stories={localClips}
                    startIndex={activeIndex}
                    onClose={() => setOpenStory(false)}
                    onDeleteStory={handleDeleteStory}
                    showMenuOption={true}
                />
            )}
        </div>
    );
}

function ProgressCircle({ progress }) {
    const radius = 40;
    const stroke = 4;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset =
        circumference - (progress / 100) * circumference;

    return (
        <svg height={radius * 2} width={radius * 2}>
            <circle
                stroke="#444"
                fill="transparent"
                strokeWidth={stroke}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
            />
            <circle
                stroke="#ef4444"
                fill="transparent"
                strokeWidth={stroke}
                strokeDasharray={`${circumference} ${circumference}`}
                style={{ strokeDashoffset }}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
            />
            <text
                x="50%"
                y="50%"
                dominantBaseline="middle"
                textAnchor="middle"
                fill="white"
                fontSize="12"
            >
                {progress}%
            </text>
        </svg>
    );
}